import React from 'react';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 16, color: '#f9fafb' }}>{title}</h2>
    <div style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: '0.95rem' }}>{children}</div>
  </div>
);

const PrivacyPage: React.FC = () => {
  return (
    <main style={{ paddingTop: 100 }}>
      <div className="container" style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px 80px' }}>
        <div style={{ marginBottom: 48 }}>
          <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 900, marginBottom: 12 }}>Privacy Policy</h1>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Last updated: May 21, 2026</p>
        </div>

        <div style={{
          background: 'rgba(37,99,235,0.1)',
          border: '1px solid rgba(37,99,235,0.3)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 40,
          color: '#93c5fd',
          fontSize: '0.9rem',
          lineHeight: 1.7,
        }}>
          <strong>🔒 Your privacy matters.</strong> PRETTIFAI accesses your Gmail only to find and process invoices. We do not read, store, or analyze any other emails. You can revoke access at any time from your Google account settings.
        </div>
        <div style={{
          background: 'rgba(234,179,8,0.08)',
          border: '1px solid rgba(234,179,8,0.25)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 40,
          color: '#fde68a',
          fontSize: '0.85rem',
          lineHeight: 1.7,
        }}>
          <strong>📋 Google API Limited Use Disclosure.</strong> PRETTIFAI's use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" style={{ color: '#fbbf24' }} target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.
        </div>

        <Section title="1. Introduction">
          <p>This Privacy Policy describes how PRETTIFAI Ltd. ("PRETTIFAI", "we", "us", or "our") collects, uses, and protects your personal data when you use our invoice management service. This policy is compliant with the General Data Protection Regulation (GDPR) and the Israeli Privacy Protection Law, 5741-1981.</p>
        </Section>

        <Section title="2. Data Controller">
          <p>PRETTIFAI Ltd. is the data controller for personal data processed through our Service. Submit a <a href="/contact" style={{ color: "#60a5fa" }}>privacy inquiry</a> via our contact form.</p>
        </Section>

        <Section title="3. Data We Collect">
          <p style={{ marginBottom: 16 }}><strong style={{ color: '#f9fafb' }}>Account Data:</strong> When you register, we collect your name, email address, and Google account identifier.</p>
          <p style={{ marginBottom: 16 }}><strong style={{ color: '#f9fafb' }}>Gmail Access:</strong> We access your Gmail account to search for invoice-related emails and attachments. We only process emails that our AI identifies as containing invoices, receipts, or billing information. We do NOT read, store, or analyze personal correspondence or non-invoice emails.</p>
          <p style={{ marginBottom: 16 }}><strong style={{ color: '#f9fafb' }}>Invoice Data:</strong> We extract and store structured invoice data including vendor names, amounts, dates, and line items for your use within the platform.</p>
          <p style={{ marginBottom: 16 }}><strong style={{ color: '#f9fafb' }}>Usage Data:</strong> We collect anonymized usage analytics (pages visited, features used, session duration) to improve the Service.</p>
          <p><strong style={{ color: '#f9fafb' }}>Payment Data:</strong> Payment information is processed exclusively by Paddle. We do not store credit card numbers or banking information.</p>
        </Section>

        <Section title="4. Legal Basis for Processing">
          <p style={{ marginBottom: 12 }}>We process your data based on:</p>
          <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li><strong style={{ color: '#f9fafb' }}>Contract performance:</strong> Processing necessary to provide the Service you subscribed to</li>
            <li><strong style={{ color: '#f9fafb' }}>Consent:</strong> Gmail access granted via OAuth2 (revocable at any time)</li>
            <li><strong style={{ color: '#f9fafb' }}>Legitimate interests:</strong> Service improvement, security monitoring, and fraud prevention</li>
            <li><strong style={{ color: '#f9fafb' }}>Legal obligation:</strong> Compliance with applicable laws and regulations</li>
          </ul>
        </Section>

        <Section title="5. How We Use Your Data">
          <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Providing and operating the invoice detection and extraction service</li>
            <li>Sending transactional emails (receipts, alerts, feature updates)</li>
            <li>Improving AI accuracy and service quality</li>
            <li>Detecting and preventing fraud, abuse, or security incidents</li>
            <li>Complying with legal obligations</li>
          </ul>
        </Section>

        <Section title="6. Google API Scopes &amp; Why We Need Them">
          <p style={{ marginBottom: 16 }}>PRETTIFAI requests the following Google API permissions. We request only the minimum scopes necessary for the service to function:</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ background: '#0f172a', borderRadius: 8, padding: 16 }}>
              <code style={{ background: '#1e293b', padding: '2px 8px', borderRadius: 4, fontSize: '0.85rem' }}>gmail.readonly</code>
              <p style={{ marginTop: 10, marginBottom: 0 }}>Used to read Gmail messages and identify emails that contain invoices, receipts, or billing documents. We only process emails our AI classifies as financial documents — we never read, store, or process personal correspondence or any other email content.</p>
            </div>

            <div style={{ background: '#0f172a', borderRadius: 8, padding: 16 }}>
              <code style={{ background: '#1e293b', padding: '2px 8px', borderRadius: 4, fontSize: '0.85rem' }}>gmail.modify</code>
              <p style={{ marginTop: 10, marginBottom: 0 }}>Used only when you choose year backfill: we apply the PRETTIFAI label to existing invoice emails already in your inbox (batch label update). We do not delete emails or change message bodies. Ongoing monitoring uses filters and push notifications — not continuous inbox modification.</p>
            </div>

            <div style={{ background: '#0f172a', borderRadius: 8, padding: 16 }}>
              <code style={{ background: '#1e293b', padding: '2px 8px', borderRadius: 4, fontSize: '0.85rem' }}>gmail.labels</code>
              <p style={{ marginTop: 10, marginBottom: 0 }}>Used to create a dedicated &quot;PRETTIFAI&quot; label in your Gmail. Applying this label to an email (manually or via our filter) is how you trigger processing. This keeps your inbox organized and gives you a simple way to interact with the system.</p>
            </div>

            <div style={{ background: '#0f172a', borderRadius: 8, padding: 16 }}>
              <code style={{ background: '#1e293b', padding: '2px 8px', borderRadius: 4, fontSize: '0.85rem' }}>gmail.settings.basic</code>
              <p style={{ marginTop: 10, marginBottom: 0 }}>Used to create a Gmail filter that routes only invoice-relevant emails (identified by keywords and sender patterns) into the PRETTIFAI label. This is central to our privacy-first design: instead of continuously scanning the entire inbox, we intercept only emails likely to contain invoices. This minimizes the amount of user data that ever reaches our system and ensures irrelevant personal emails are never processed.</p>
            </div>

            <div style={{ background: '#0f172a', borderRadius: 8, padding: 16 }}>
              <code style={{ background: '#1e293b', padding: '2px 8px', borderRadius: 4, fontSize: '0.85rem' }}>Real-time invoice alerts</code>
              <p style={{ marginTop: 10, marginBottom: 8 }}>Lets Prettifai know when a new invoice email lands in your inbox, so we can organize it in the background without you doing anything. You don&apos;t need to forward messages, upload files, or keep checking back — new invoices are picked up as they arrive.</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>Google permission: <code style={{ fontSize: '0.75rem' }}>https://www.googleapis.com/auth/pubsub</code></p>
            </div>

            <div style={{ background: '#0f172a', borderRadius: 8, padding: 16 }}>
              <code style={{ background: '#1e293b', padding: '2px 8px', borderRadius: 4, fontSize: '0.85rem' }}>userinfo.email</code>
              <p style={{ marginTop: 10, marginBottom: 0 }}>Used during Gmail connect to read your Google account email address so we know which mailbox was linked to your Prettifai account. We do not use this permission to access your name, photo, or other profile information.</p>
            </div>
          </div>

          <p style={{ marginTop: 16 }}>Our use of Google user data complies with the <a href="https://developers.google.com/terms/api-services-user-data-policy" style={{ color: '#60a5fa' }} target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</p>
        </Section>

        <Section title="7. Data Sharing and Third Parties">
          <p style={{ marginBottom: 12 }}>We do not sell your personal data. We may share data with:</p>
          <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li><strong style={{ color: '#f9fafb' }}>Paddle:</strong> For payment processing (as Merchant of Record)</li>
            <li><strong style={{ color: '#f9fafb' }}>Cloud infrastructure providers:</strong> For hosting and storage (data encrypted at rest)</li>
            <li><strong style={{ color: '#f9fafb' }}>AI model providers:</strong> Invoice data may be processed by AI APIs under strict data processing agreements</li>
            <li><strong style={{ color: '#f9fafb' }}>Legal authorities:</strong> When required by law, court order, or to protect rights and safety</li>
          </ul>
        </Section>

        <Section title="8. Data Retention">
          <p>We retain your account and invoice data for as long as your account is active, plus 30 days after account deletion or termination. You may request deletion of your data at any time. Anonymized usage analytics may be retained indefinitely.</p>
        </Section>

        <Section title="9. Your Rights (GDPR)">
          <p style={{ marginBottom: 12 }}>If you are in the European Economic Area or Israel, you have the following rights:</p>
          <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li><strong style={{ color: '#f9fafb' }}>Access:</strong> Request a copy of your personal data</li>
            <li><strong style={{ color: '#f9fafb' }}>Rectification:</strong> Correct inaccurate personal data</li>
            <li><strong style={{ color: '#f9fafb' }}>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
            <li><strong style={{ color: '#f9fafb' }}>Portability:</strong> Receive your data in a machine-readable format</li>
            <li><strong style={{ color: '#f9fafb' }}>Restriction:</strong> Limit how we process your data</li>
            <li><strong style={{ color: '#f9fafb' }}>Objection:</strong> Object to processing based on legitimate interests</li>
          </ul>
          <p style={{ marginTop: 12 }}>To exercise these rights, submit a request via our <a href="/contact" style={{ color: "#60a5fa" }}>contact form</a>. We will respond within 30 days.</p>
        </Section>

        <Section title="10. Security">
          <p>We implement industry-standard security measures including TLS encryption in transit, AES-256-GCM encryption for OAuth credentials at rest, provider-level volume encryption for stored data, regular security audits, and role-based access controls. However, no system is 100% secure. In the event of a data breach affecting your rights, we will notify you within 72 hours as required by GDPR.</p>
        </Section>

        <Section title="11. Cookies">
          <p>We use essential cookies for authentication and session management. We use analytics cookies to understand usage patterns. You can disable non-essential cookies in your browser settings without affecting core functionality.</p>
        </Section>

        <Section title="12. Children's Privacy">
          <p>The Service is not directed to individuals under 18 years of age. We do not knowingly collect personal data from children. If you become aware that a child has provided us with personal data, please contact us immediately.</p>
        </Section>

        <Section title="13. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. We will notify you of significant changes via email at least 14 days before they take effect. The "last updated" date at the top of this policy reflects the most recent revision.</p>
        </Section>

        <Section title="14. Contact Us">
          <p>
            <strong style={{ color: '#f9fafb' }}>PRETTIFAI Ltd.</strong><br />
            Privacy &amp; data inquiries: <a href="/contact" style={{ color: "#60a5fa" }}>Contact Form</a><br />
            Data deletion requests: <a href="/contact" style={{ color: "#60a5fa" }}>Contact Form (select Privacy)</a>
          </p>
        </Section>
      </div>
    </main>
  );
};

export default PrivacyPage;
